/**
 * LeaveCalendar — month grid showing leaves (approved + pending), holidays,
 * and weekend/today markers. Generic enough to be reused for the personal
 * view (Mon Espace) and the team view (Équipe).
 *
 * The owning component decides what to render in each cell via the
 * `entriesByDate` map (keyed YYYY-MM-DD). This component owns the layout +
 * navigation, not the data fetching.
 */

import React, { useMemo } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import type { LeaveType } from '../../api/leave.api';
import './LeaveCalendar.css';

export interface CalendarEntry {
  // What renders inside a cell. The component groups by date.
  kind: 'holiday' | 'leave';
  label: string;             // displayed text (e.g. "Lundi de Pâques", "Léa C.", "Congé légal")
  type?: LeaveType;          // for leaves: drives the colour
  status?: 'pending' | 'approved' | 'denied' | 'cancelled';
  fraction?: number;         // 1 or 0.5
  userId?: string;           // for team view: distinct color hue per user
  tooltip?: string;          // hover full text
  onClick?: () => void;      // optional cell-level click handler
}

interface LeaveCalendarProps {
  year: number;
  month: number;             // 0-indexed (0 = Jan)
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onToday: () => void;
  entriesByDate: Map<string, CalendarEntry[]>;
  onDayClick?: (dateIso: string) => void;
}

const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];
const DAY_NAMES_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

// Deterministic pastel for team-view per-user colors
function userColor(userId?: string): string {
  if (!userId) return '#dbeafe';
  const palette = ['#dbeafe', '#fce7f3', '#d1fae5', '#fed7aa', '#e9d5ff', '#fee2e2', '#cffafe', '#fef3c7'];
  let h = 0;
  for (let i = 0; i < userId.length; i++) h = (h * 31 + userId.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

const TYPE_BG: Record<LeaveType, { bg: string; fg: string }> = {
  legal:  { bg: '#dbeafe', fg: '#1e3a8a' },
  recup:  { bg: '#cffafe', fg: '#155e75' },
  unpaid: { bg: '#f3f4f6', fg: '#374151' },
  sick:   { bg: '#fce7f3', fg: '#9d174d' },
};

function ymd(year: number, month: number, day: number) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export const LeaveCalendar: React.FC<LeaveCalendarProps> = ({
  year, month, onPrevMonth, onNextMonth, onToday, entriesByDate, onDayClick,
}) => {
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // Build 6×7 grid covering the month, padded with prev/next month days
  const cells = useMemo(() => {
    const first = new Date(Date.UTC(year, month, 1));
    // Monday=0 ... Sunday=6
    const firstDow = (first.getUTCDay() + 6) % 7;
    const gridStart = new Date(first);
    gridStart.setUTCDate(gridStart.getUTCDate() - firstDow);

    const cells: { date: Date; iso: string; isOtherMonth: boolean; isWeekend: boolean }[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setUTCDate(d.getUTCDate() + i);
      cells.push({
        date: d,
        iso: ymd(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()),
        isOtherMonth: d.getUTCMonth() !== month,
        isWeekend: i % 7 >= 5,
      });
    }
    // Drop last row if all in other month (typical 6-week padding)
    while (cells.length > 28 && cells.slice(-7).every(c => c.isOtherMonth)) {
      cells.splice(-7);
    }
    return cells;
  }, [year, month]);

  return (
    <div className="leave-cal">
      <div className="leave-cal-header">
        <button className="leave-cal-nav" onClick={onPrevMonth} title="Mois précédent">
          <ChevronLeftIcon className="w-4 h-4" />
        </button>
        <h3 className="leave-cal-title">{MONTHS_FR[month]} {year}</h3>
        <button className="leave-cal-nav" onClick={onNextMonth} title="Mois suivant">
          <ChevronRightIcon className="w-4 h-4" />
        </button>
        <button className="leave-cal-today" onClick={onToday}>Aujourd'hui</button>
      </div>

      <div className="leave-cal-grid">
        {DAY_NAMES_FR.map(name => (
          <div key={name} className="leave-cal-dow">{name}</div>
        ))}
        {cells.map(c => {
          const entries = entriesByDate.get(c.iso) || [];
          const isToday = c.iso === todayIso;
          const classes = [
            'leave-cal-cell',
            c.isOtherMonth && 'is-other-month',
            c.isWeekend && 'is-weekend',
            isToday && 'is-today',
            entries.length > 0 && 'has-entries',
          ].filter(Boolean).join(' ');

          return (
            <div
              key={c.iso}
              className={classes}
              onClick={() => onDayClick?.(c.iso)}
              data-date={c.iso}
            >
              <div className="leave-cal-day-num">{c.date.getUTCDate()}</div>
              <div className="leave-cal-entries">
                {entries.slice(0, 3).map((e, i) => {
                  let bg = '#e5e7eb', fg = '#374151';
                  if (e.kind === 'holiday') {
                    bg = '#fef3c7'; fg = '#92400e';
                  } else if (e.userId) {
                    bg = userColor(e.userId);
                    fg = '#1e3a8a';
                  } else if (e.type) {
                    bg = TYPE_BG[e.type].bg;
                    fg = TYPE_BG[e.type].fg;
                  }
                  const isPending = e.kind === 'leave' && e.status === 'pending';
                  return (
                    <div
                      key={i}
                      className={`leave-cal-chip ${isPending ? 'is-pending' : ''}`}
                      style={{ background: bg, color: fg }}
                      title={e.tooltip || e.label}
                      onClick={ev => {
                        if (e.onClick) { ev.stopPropagation(); e.onClick(); }
                      }}
                    >
                      {e.fraction && e.fraction < 1 && <span className="leave-cal-chip-half">½</span>}
                      <span className="leave-cal-chip-label">{e.label}</span>
                    </div>
                  );
                })}
                {entries.length > 3 && (
                  <div className="leave-cal-more">+{entries.length - 3}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LeaveCalendar;
